import {getSessionGeneration} from './sessionGeneration.js'

const pending = new Map()
const keyFor = (input, actorId) => `sr_school_team_pending:${actorId}:${input.season}:${input.school}`
const same = (a, b) => JSON.stringify([...a].sort()) === JSON.stringify([...b].sort())

export function schoolTeamPayload(input, candidates, capabilities = {}) {
  const {school, conference, season, expectedVersion, reason, members, action = 'set'} = input
  if (!school || !conference || !Number.isInteger(season) || !Number.isSafeInteger(expectedVersion) || expectedVersion < 0) return {error:'Refresh the school, season and current desk version before saving.'}
  if (typeof reason !== 'string' || reason.trim().length < 5) return {error:'Say why, in a sentence.'}
  if (!['set', 'clear'].includes(action)) return {error:'Unknown school team action.'}
  const selected = [...new Set(members || [])]
  if (action === 'set') {
    if (!selected.length || selected.some(id => !candidates.some(c => c.id === id))) return {error:'Choose active people from the current school-team list.'}
    if (selected.some(id => candidates.find(c => c.id === id)?.identityKind === 'staff') && capabilities.staffTeamWritesEnabled !== true) return {error:'Staff-member editing is disabled until the compatible release and enrollment are approved.'}
    if (selected.some(id => { const c = candidates.find(candidate => candidate.id === id); return Object.hasOwn(c, 'pairedWithId') && (!c.pairedWithId || !candidates.some(partner => partner.id === c.pairedWithId)) })) return {error:'A required standing support partner is unavailable. No incomplete school team can be saved.'}
    if (!selected.some(id => candidates.find(c => c.id === id)?.role === 'sales')) return {error:'Choose at least one sales desk.'}
    if (selected.some(id => { const pair = candidates.find(c => c.id === id)?.pairedWithId; return pair && !selected.includes(pair) })) return {error:'Include the standing support partner for every selected desk.'}
  }
  return {mode:'school-team',scope:'school',school,conference,season,expectedVersion,reason:reason.trim(),members:action === 'clear' ? [] : selected,action}
}

export function matchesSchoolTeamReceipt(pick, body) {
  return !!pick?.id && pick.requestId === body.requestId && pick.kind === 'school-team'
    && pick.scope === 'school' && pick.target === body.school && pick.conference === body.conference
    && Number(pick.season) === body.season && pick.action === body.action
    && Array.isArray(pick.members) && same(pick.members.map(m => m.id), body.members)
}

// Retain uncertain operations across row unmounts within this signed-in session.
// Never replay a write automatically; reconciliation only reads its receipt.
export function createSchoolTeamController({send, read, onState, onSaved, actorId = '', storage = globalThis.sessionStorage, makeRequestId = () => globalThis.crypto.randomUUID()}) {
  let active = true, busy = false
  const generation = getSessionGeneration()
  const current = () => active && generation === getSessionGeneration()
  const say = state => { if (current()) onState(state) }
  const getPending = key => {
    if (pending.has(key)) return pending.get(key)
    try { const body = JSON.parse(storage?.getItem(key) || 'null'); if (body) pending.set(key,body); return body } catch { return null }
  }
  const clearPending = key => {pending.delete(key); storage?.removeItem(key)}
  return {
    dispose() { active = false },
    hasPending(input) { return !!getPending(keyFor(input,actorId)) },
    async reconcile(input) {
      const key = keyFor(input,actorId), body = getPending(key)
      if (!body || busy || !current()) return false
      busy = true
      try {
        const result = await read()
        const receipt = result?.picks?.find(p => p.requestId === body.requestId)
        if (result?.durable !== true || !matchesSchoolTeamReceipt(receipt, body)) {
          say({status:'uncertain',msg:'No matching durable receipt was found. Keep this operation pending; do not repeat it.'})
          return false
        }
        clearPending(key)
        say({status:'done',msg:'Saved school team verified in durable history. Refreshing the board.'})
        if (current()) onSaved?.(result)
        return true
      } catch {
        say({status:'uncertain',msg:'History could not be verified. Do not repeat the save.'})
        return false
      } finally { busy = false }
    },
    async submit(input, candidates, capabilities = {}) {
      if (!current() || busy) return false
      const key = keyFor(input,actorId)
      if (getPending(key)) { say({status:'uncertain',msg:'A prior save needs reconciliation. Read its saved history before continuing.'}); return false }
      const body = schoolTeamPayload(input,candidates,capabilities)
      if (body.error) { say({status:'error',msg:body.error}); return false }
      body.requestId = makeRequestId()
      if (!body.requestId) { say({status:'error',msg:'A request identity could not be created.'}); return false }
      // Keep only receipt identity, never the reason, credentials or catalog.
      try { if (storage) {if (!actorId) throw Error('Missing actor'); const identity = {...body}; delete identity.reason; storage.setItem(key,JSON.stringify(identity))} }
      catch {say({status:'error',msg:'This browser cannot retain recovery information. No save was sent.'}); return false}
      pending.set(key,body); busy = true
      say({status:'saving',msg:'Saving school team…'})
      try {
        const result = await send(body)
        if (result?.ok !== true || result?.durable !== true || !matchesSchoolTeamReceipt(result.pick,body)) throw new Error('No matching durable receipt.')
        clearPending(key)
        say({status:'done',msg:'School team saved. Refreshing the board.'})
        if (current()) onSaved?.(result)
        return true
      } catch (error) {
        const disabledRefusal = error?.status === 503 && ['school_team_writes_disabled','staff_team_writes_disabled'].includes(error?.body?.error)
          && error?.body?.retrySafe === true && error?.body?.uncertain === false
        const uncertain = error?.body?.uncertain === true || error?.body?.retrySafe === false || (!disabledRefusal && ![400,401,403,404,409,422].includes(error?.status))
        if (!uncertain) clearPending(key)
        say({status:uncertain ? 'uncertain' : 'error',msg:uncertain ? 'The save outcome is uncertain. Read saved history before making another change.' : disabledRefusal ? 'School-team editing is disabled on the server. No change was saved.' : error?.body?.detail || error.message || 'The school team was refused.'})
        return false
      } finally { busy = false }
    },
  }
}
